import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { DatePicker, TimePicker, message } from "antd";
import type { TimePickerProps } from "antd";
import dayjs, { Dayjs } from "dayjs";
import editIconOrange from "assets/svg/edit-icon-orange.svg";
import moment from "moment";
import "moment-timezone"; // ensure tz helpers are available
import { SearchInput } from "../Inputs/SearchInput";
import cancelIconOrange from "assets/svg/cancelIconOrange.svg";
import { toast } from "sonner";

const DATE_FMT = "YYYY-MM-DD";
const TIME_FMT_12 = "hh:mm A";
const TIME_FMT_24 = "HH:mm";

type Nullable<T> = T | null;

export type DateTimeRangeValue = {
  fromDate: Nullable<Dayjs>;
  fromTime: Nullable<Dayjs>;
  toDate: Nullable<Dayjs>;
  toTime: Nullable<Dayjs>;
};

export type DateTimeRangeOutput = DateTimeRangeValue & {
  start: Nullable<Dayjs>;
  end: Nullable<Dayjs>;
};

export type DateTimeRangeCardProps = {
  value?: Partial<DateTimeRangeValue>;
  defaultValue?: Partial<DateTimeRangeValue>;
  onChange?: (next: DateTimeRangeOutput) => void;
  minuteStep?: TimePickerProps["minuteStep"];
  use12Hours?: boolean;
  timezoneLabel?: string;
  onEditTimezone?: () => void;
  editIconSrc?: string;
  className?: string;
  autoExtendByHour?: boolean;
  activeTimezone?: string;
  setActiveTimeZone?: (val: string) => void;
  setSelectedTimeZone?: (val: string) => void;
  onFormattedChange?: (next: {
    fromDate: string | null;
    fromTime: string | null;
    toDate: string | null;
    toTime: string | null;
    start: string | null;
    end: string | null;
  }) => void;
  hideTimezone?: boolean;
  hideAllLabel?: boolean;
  /** All dates strictly AFTER this date are disabled */
  maxDate?: Dayjs | string | Date;
};

const range = (start: number, end: number) =>
  Array.from({ length: end - start }, (_, i) => start + i);

const combineDateTime = (
  date: Nullable<Dayjs>,
  time: Nullable<Dayjs>
): Nullable<Dayjs> => {
  if (!date || !time) return null;
  return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
};

const todayStart = () => dayjs().startOf("day");

// Round "now" up to the next minuteStep boundary
const getDefaultStartTime = (minuteStep: number = 15) => {
  const now = dayjs().second(0).millisecond(0);
  const remainder = now.minute() % minuteStep;
  const delta = remainder === 0 ? 0 : minuteStep - remainder;
  return now.add(delta, "minute");
};

// Helper: anchor a picked time (whose date is usually "today") to a given Dayjs date
const anchorTimeToDate = (date: Dayjs | null, time: Dayjs | null) => {
  if (!date || !time) return time; // or null
  return date.hour(time.hour()).minute(time.minute()).second(0).millisecond(0);
};

export default function DateTimeRangeCard({
  value,
  defaultValue,
  onChange,
  minuteStep = 15 as const,
  use12Hours = false,
  timezoneLabel,
  onEditTimezone,
  editIconSrc,
  className = "",
  autoExtendByHour = true,
  activeTimezone,
  setSelectedTimeZone,
  setActiveTimeZone,
  onFormattedChange,
  hideTimezone,
  hideAllLabel,
  maxDate,
}: DateTimeRangeCardProps) {
  // Normalize maxDate to startOf("day")
  const maxDateDJ: Dayjs | null = useMemo(() => {
    if (!maxDate) return null;
    if (dayjs.isDayjs(maxDate)) return maxDate.startOf("day");
    if (maxDate instanceof Date) return dayjs(maxDate).startOf("day");
    return dayjs(maxDate).startOf("day"); // string
  }, [maxDate]);

  // Defaults (with auto-extension)
  const defaultStartTime = getDefaultStartTime(minuteStep);
  const defaultEndTime = defaultStartTime.add(1, "hour");

  const [showTimezonDropDown, setShowTimezonDropDown] = useState(false);

  const timeZonesWithOffset = useMemo(() => {
    return moment.tz.names().map((tz) => {
      const offset = moment.tz(tz).utcOffset();
      const offsetHours = offset / 60;
      const sign = offsetHours >= 0 ? "+" : "-";
      return `UTC${sign}${Math.abs(offsetHours)} — ${tz}`;
    });
  }, []);

  const [filteredTimeZone, setFilteredTimeZone] =
    useState<string[]>(timeZonesWithOffset);

  // Track if user manually edited To (prevents auto-overwriting on From edits)
  const [userTouchedTo, setUserTouchedTo] = useState(false);

  // Local, always-updating state
  const [fromDate, setFromDate] = useState<Nullable<Dayjs>>(
    value?.fromDate ?? defaultValue?.fromDate ?? dayjs()
  );
  const [fromTime, setFromTime] = useState<Nullable<Dayjs>>(
    value?.fromTime ?? defaultValue?.fromTime ?? defaultStartTime
  );
  const [toDate, setToDate] = useState<Nullable<Dayjs>>(
    value?.toDate ?? defaultValue?.toDate ?? dayjs()
  );
  const [toTime, setToTime] = useState<Nullable<Dayjs>>(
    value?.toTime ??
      defaultValue?.toTime ??
      (autoExtendByHour ? defaultEndTime : null)
  );

  // Apply defaultValue ONCE on mount (so it doesn't fight the user's picks)
  const didInitFromDefault = useRef(false);
  useEffect(() => {
    if (didInitFromDefault.current) return;
    if (!defaultValue) return;

    if ("fromDate" in defaultValue) setFromDate(defaultValue.fromDate ?? null);
    if ("fromTime" in defaultValue) setFromTime(defaultValue.fromTime ?? null);
    if ("toDate" in defaultValue) setToDate(defaultValue.toDate ?? null);
    if ("toTime" in defaultValue) setToTime(defaultValue.toTime ?? null);
    didInitFromDefault.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);


  // If parent controls via `value`, mirror it
  useEffect(() => {
    if (!value) return;
    if ("fromDate" in value) setFromDate(value.fromDate ?? null);
    if ("fromTime" in value) setFromTime(value.fromTime ?? null);
    if ("toDate" in value) setToDate(value.toDate ?? null);
    if ("toTime" in value) setToTime(value.toTime ?? null);
  }, [value?.fromDate, value?.fromTime, value?.toDate, value?.toTime]);

  // Clamp dates if they exceed maxDate
  useEffect(() => {
    if (!maxDateDJ) return;

    let mutated = false;
    let nextFromDate = fromDate;
    let nextToDate = toDate;

    if (fromDate && fromDate.startOf("day").isAfter(maxDateDJ)) {
      nextFromDate = maxDateDJ;
      mutated = true;
    }
    if (toDate && toDate.startOf("day").isAfter(maxDateDJ)) {
      nextToDate = maxDateDJ;
      mutated = true;
    }

    if (mutated) {
      setFromDate(nextFromDate ?? null);
      setToDate(nextToDate ?? null);
      if (nextFromDate && fromTime && autoExtendByHour && !userTouchedTo) {
        const newEnd = fromTime.add(1, "hour");
        setToTime(newEnd);
      }
      emit({ fromDate: nextFromDate ?? null, toDate: nextToDate ?? null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maxDateDJ]);

  const start = useMemo(
    () => combineDateTime(fromDate, fromTime),
    [fromDate, fromTime]
  );
  const end = useMemo(() => combineDateTime(toDate, toTime), [toDate, toTime]);

  const emit = useCallback(
    (patch?: Partial<DateTimeRangeValue>) => {
      const nextFromDate = patch?.fromDate ?? fromDate ?? null;
      const nextFromTime = patch?.fromTime ?? fromTime ?? null;
      const nextToDate = patch?.toDate ?? toDate ?? null;
      const nextToTime = patch?.toTime ?? toTime ?? null;

      const startDJ = combineDateTime(nextFromDate, nextFromTime);
      const endDJ = combineDateTime(nextToDate, nextToTime);

      onChange?.({
        fromDate: nextFromDate,
        fromTime: nextFromTime,
        toDate: nextToDate,
        toTime: nextToTime,
        start: startDJ,
        end: endDJ,
      });

      const timeFmt = use12Hours ? TIME_FMT_12 : TIME_FMT_24;
      onFormattedChange?.({
        fromDate: nextFromDate ? nextFromDate.format(DATE_FMT) : null,
        fromTime: nextFromTime ? nextFromTime.format(timeFmt) : null,
        toDate: nextToDate ? nextToDate.format(DATE_FMT) : null,
        toTime: nextToTime ? nextToTime.format(timeFmt) : null,
        start: startDJ ? startDJ.format(`${DATE_FMT} ${timeFmt}`) : null,
        end: endDJ ? endDJ.format(`${DATE_FMT} ${timeFmt}`) : null,
      });
    },
    [
      fromDate,
      fromTime,
      toDate,
      toTime,
      onChange,
      onFormattedChange,
      use12Hours,
    ]
  );

  const handleGetTimeZoneLabel = (input?: string): string => {
    let timeZone = "";

    if (input?.includes("—")) {
      const parts = input.split("—").map((part) => part.trim());
      timeZone = parts[1];
    } else if (input?.includes("/")) {
      timeZone = input.trim();
    } else {
      timeZone = moment.tz.guess();
    }

    const offsetMinutes = moment.tz(timeZone).utcOffset();
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const hours = String(Math.floor(Math.abs(offsetMinutes) / 60)).padStart(
      2,
      "0"
    );
    const minutes = String(Math.abs(offsetMinutes) % 60).padStart(2, "0");
    const offsetFormatted = `GMT ${sign}${hours}:${minutes}`;

    const city = timeZone.split("/")[1]?.replace(/_/g, " ") ?? timeZone;

    return `${offsetFormatted} ${city} Time`;
  };

  // Disable picking a FROM date in the past OR after maxDate
  const disableFromDate = (current: Dayjs) => {
    if (!current) return false;
    const isPast = current.startOf("day").isBefore(todayStart());
    const isAfterMax = !!maxDateDJ && current.startOf("day").isAfter(maxDateDJ);
    return isPast || isAfterMax;
  };

  // Disable TO dates before FROM date and after maxDate
  const disableToDate = (current: Dayjs) => {
    if (!current) return false;
    const min = (fromDate ?? dayjs()).startOf("day");
    const isBeforeFrom = current.startOf("day").isBefore(min);
    const isAfterMax = !!maxDateDJ && current.startOf("day").isAfter(maxDateDJ);
    return isBeforeFrom || isAfterMax;
  };

  // FROM time: if choosing today, disable times earlier than now
  const disabledFromTime = () => {
    if (!fromDate || !fromDate.isSame(dayjs(), "day")) return {} as any;
    const now = dayjs();
    return {
      disabledHours: () => range(0, now.hour()),
      disabledMinutes: (h: number) =>
        h === now.hour() ? range(0, now.minute()) : [],
    } as any;
  };

  // TO time: when same day, only allow times strictly after the start time
  const disabledToTime = () => {
    if (!fromDate || !toDate || !fromTime) return {} as any;
    if (toDate.isSame(fromDate, "day")) {
      const startHour = fromTime.hour();
      const startMinute = fromTime.minute();
      return {
        disabledHours: () => range(0, startHour),
        disabledMinutes: (h: number) =>
          h === startHour ? range(0, startMinute + 1) : [],
      } as any;
    }
    return {} as any;
  };

  // ===== Handlers (ALWAYS set local state, then emit) =====
  const handleFromDateChange = (d: Dayjs | null, _str: string | string[]) => {
    if (d && maxDateDJ && d.startOf("day").isAfter(maxDateDJ)) {
      toast.error("Date cannot be after the maximum allowed date.");
      return;
    }

    // when From date changes, it's safe to re-derive To again
    setUserTouchedTo(false);

    const clamped = d ?? null;
    const patch: Partial<DateTimeRangeValue> = { fromDate: clamped };

    // If choosing today and existing fromTime is in the past, bump to next step
    if (
      clamped?.isSame(dayjs(), "day") &&
      fromTime &&
      fromTime.isBefore(dayjs(), "minute")
    ) {
      const newStartTime = getDefaultStartTime(minuteStep);
      patch.fromTime = anchorTimeToDate(clamped, newStartTime);
    }

    // Keep To aligned if now before From, and only auto-extend when not user-touched
    if (clamped && toDate && toDate.isBefore(clamped, "day")) {
      patch.toDate = clamped;
      if ((patch.fromTime ?? fromTime) && autoExtendByHour && !userTouchedTo) {
        const base = patch.fromTime ?? fromTime!;
        patch.toTime = anchorTimeToDate(clamped, base.add(1, "hour"));
      }
    }

    // Enforce maxDate on To
    if (maxDateDJ && (patch.toDate ?? toDate)?.isAfter(maxDateDJ, "day")) {
      patch.toDate = maxDateDJ;
    }

    setFromDate(patch.fromDate ?? null);
    if ("fromTime" in patch) setFromTime(patch.fromTime ?? null);
    if ("toDate" in patch) setToDate(patch.toDate ?? null);
    if ("toTime" in patch) setToTime(patch.toTime ?? null);

    emit(patch);
  };

  const handleFromTimeChange = (t: Dayjs | null, _str: string | string[]) => {
  // Anchor picked time to fromDate (or today if empty)
  const anchoredFromTime = t
    ? anchorTimeToDate(fromDate ?? dayjs(), t)
    : null;

  const patch: Partial<DateTimeRangeValue> = { fromTime: anchoredFromTime };

  if (anchoredFromTime && autoExtendByHour && !userTouchedTo) {
    const newEndTime = anchoredFromTime.add(1, "hour");
    patch.toTime = newEndTime;

    // --- Only touch toDate if we need to ---
    // Prefer keeping the user's current toDate if it exists
    const baseFromDate = fromDate ?? dayjs();

    const wouldCrossToNextDay = !newEndTime.isSame(baseFromDate, "day");
    const toDateIsEmpty = !toDate;

    if (toDateIsEmpty || wouldCrossToNextDay) {
      let nextToDate = wouldCrossToNextDay
        ? baseFromDate.add(1, "day")
        : baseFromDate;

      if (maxDateDJ && nextToDate.isAfter(maxDateDJ, "day")) {
        nextToDate = maxDateDJ;
      }

      patch.toDate = nextToDate;
    }
  }

  // Same-day guard when not auto-extending
  if (
    anchoredFromTime &&
    toDate &&
    fromDate &&
    toDate.isSame(fromDate, "day") &&
    toTime &&
    !toTime.isAfter(anchoredFromTime, "minute") &&
    !autoExtendByHour
  ) {
    patch.toTime = null;
  }

  setFromTime(patch.fromTime ?? null);
  if ("toTime" in patch) setToTime(patch.toTime ?? null);
  if ("toDate" in patch) setToDate(patch.toDate ?? null);
  emit(patch);
};


  const handleToDateChange = (d: Dayjs | null, _str: string | string[]) => {
    setUserTouchedTo(true);

    if (d && maxDateDJ && d.startOf("day").isAfter(maxDateDJ)) {
      message.error("Date cannot be after the maximum allowed date.");
      return;
    }
    const clamped = d ?? null;

    if (clamped && fromDate && clamped.isBefore(fromDate, "day")) {
      message.error("End date must be on or after the start date.");
      return;
    }

    const patch: Partial<DateTimeRangeValue> = { toDate: clamped };

    if (
      clamped &&
      fromDate &&
      clamped.isSame(fromDate, "day") &&
      toTime &&
      fromTime &&
      !toTime.isAfter(fromTime, "minute")
    ) {
      if (autoExtendByHour && !userTouchedTo) {
        patch.toTime = fromTime.add(1, "hour");
      } else {
        patch.toTime = null;
      }
    }

    setToDate(patch.toDate ?? null);
    if ("toTime" in patch) setToTime(patch.toTime ?? null);
    emit(patch);
  };

  const handleToTimeChange = (t: Dayjs | null, _str: string | string[]) => {
    setUserTouchedTo(true);

    // Anchor picked time to toDate (or fromDate / today as fallbacks)
    const anchorDate = toDate ?? fromDate ?? dayjs();
    const anchoredToTime = t ? anchorTimeToDate(anchorDate, t) : null;

    if (
      anchoredToTime &&
      fromDate &&
      toDate &&
      toDate.isSame(fromDate, "day") &&
      fromTime &&
      !anchoredToTime.isAfter(fromTime, "minute")
    ) {
      message.error("End time must be after the start time.");
      return;
    }
    const patch: Partial<DateTimeRangeValue> = { toTime: anchoredToTime };

    setToTime(patch.toTime ?? null);
    emit(patch);
  };

  const handleSearchTimezone = (val: string) => {
    const filteredVal = timeZonesWithOffset.filter((item) =>
      item.toLowerCase().includes(val.toLowerCase())
    );
    setFilteredTimeZone(filteredVal);
  };

  return (
    <div className={`bg-[#F6F6F6] p-4 rounded-lg ${className}`}>
      {/* FROM */}
      <div className="flex items-center gap-2 mb-2">
        {!hideAllLabel && <p className="text-[#AFAFAF] min-w-[48px]">From</p>}
        <div className="bg-[#EEEEEE] rounded-lg flex items-center w-full">
          <div className="w-full text-center py-3 border-r relative">
            <div
              style={{
                pointerEvents:
                  maxDateDJ && dayjs().startOf("day").isAfter(maxDateDJ)
                    ? "none"
                    : "auto",
              }}
              className="absolute opacity-0 inset-0"
            >
              <DatePicker
                disabledDate={disableFromDate}
                value={fromDate}
                onChange={handleFromDateChange}
                inputReadOnly
              />
            </div>
            <p>{fromDate ? dayjs(fromDate).format("ddd MMM DD") : "--"}</p>
          </div>

          <div className="w-full relative text-center py-3">
            <div className="absolute opacity-0 inset-0">
              <TimePicker
                format={use12Hours ? "hh:mm A" : "HH:mm"}
                minuteStep={minuteStep}
                use12Hours={use12Hours}
                showSecond={false}
                // needConfirm can make UX feel odd; omit unless required
                // needConfirm={true}
                disabledTime={disabledFromTime}
                value={fromTime}
                onChange={handleFromTimeChange}
                placeholder="Select time"
              />
            </div>
            <p>
              {fromTime
                ? dayjs(fromTime).format(use12Hours ? "hh:mm A" : "HH:mm")
                : "--:--"}
            </p>
          </div>
        </div>
      </div>

      {/* TO */}
      {!hideAllLabel && (
        <div className="flex items-center gap-2 mb-2">
          {!hideAllLabel && <p className="text-[#AFAFAF] min-w-[48px]">To</p>}
          <div className="bg-[#EEEEEE] rounded-lg flex items-center w-full">
            <div className="w-full text-center py-3 border-r relative">
              <div
                style={{
                  pointerEvents:
                    maxDateDJ && dayjs().startOf("day").isAfter(maxDateDJ)
                      ? "none"
                      : "auto",
                }}
                className="absolute opacity-0 inset-0"
              >
                <DatePicker
                  disabledDate={disableToDate}
                  value={toDate}
                  onChange={handleToDateChange}
                  inputReadOnly
                />
              </div>
              <p>{toDate ? dayjs(toDate).format("ddd MMM DD") : "--"}</p>
            </div>

            <div className="w-full relative text-center py-3">
              <div className="absolute opacity-0 inset-0">
                <TimePicker
                  format={use12Hours ? "hh:mm A" : "HH:mm"}
                  minuteStep={minuteStep}
                  use12Hours={use12Hours}
                  showSecond={false}
                  // needConfirm={true}
                  disabledTime={disabledToTime}
                  value={toTime}
                  onChange={handleToTimeChange}
                  placeholder="Select time"
                />
              </div>
              <p>
                {toTime
                  ? dayjs(toTime).format(use12Hours ? "hh:mm A" : "HH:mm")
                  : "--:--"}
              </p>
            </div>
          </div>
        </div>
      )}

      {!hideTimezone && (timezoneLabel || onEditTimezone) && (
        <div className="mt-4 flex items-center gap-2 relative">
          <p className="text-[#FF6929]">{activeTimezone ?? ""}</p>
          {onEditTimezone && editIconSrc && (
            <img
              onClick={() => {
                setShowTimezonDropDown((prev) => !prev);
              }}
              className="cursor-pointer"
              src={showTimezonDropDown ? cancelIconOrange : editIconOrange}
              alt="edit timezone"
            />
          )}
          {showTimezonDropDown && (
            <div className="absolute z-50 top-6 h-[200px] overflow-y-scroll hide-scrollbar w-[400px] bg-white rounded-lg ">
              <div className="sticky top-0 z-30 p-4 bg-white">
                <SearchInput
                  fullWidth
                  handleInputChange={handleSearchTimezone}
                />
              </div>
              <div className="px-4 pb-4">
                {filteredTimeZone.length ? (
                  filteredTimeZone.map((item) => {
                    return (
                      <p
                        key={item}
                        onClick={() => {
                          if (setActiveTimeZone) {
                            setActiveTimeZone(handleGetTimeZoneLabel(item));
                          }
                          if (setSelectedTimeZone) {
                            const parts = item.split("—"); // use em dash
                            const tzName = parts[1]?.trim();
                            if (tzName) setSelectedTimeZone(tzName);
                          }
                          setShowTimezonDropDown(false);
                        }}
                        className="text-xs font-sfMedium pb-2 cursor-pointer"
                      >
                        {item}
                      </p>
                    );
                  })
                ) : (
                  <p className="text-[#757575] py-4 text-center">
                    No data found
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
